'use client'

import { Suspense, useRef, useState } from 'react'
import { Turnstile, type TurnstileInstance } from '@marsidev/react-turnstile'
import { createLogger } from '@sim/logger'
import { useRouter, useSearchParams } from 'next/navigation'
import { useLocale, useTranslations } from 'next-intl'
import { usePostHog } from 'posthog-js/react'
import { trackFreebuffConversion } from '@/lib/analytics/freebuff'
import { trackGoogleEvent } from '@/lib/analytics/google'
import { client, useSession } from '@/lib/auth/client'
import { useTrackingConsent } from '@/lib/consent/tracking-consent'
import { getEnv, isFalsy } from '@/lib/core/config/env'
import { isSsoEnabled } from '@/lib/core/config/env-flags'
import { validateCallbackUrl } from '@/lib/core/security/input-validation'
import { quickValidateEmail } from '@/lib/messaging/email/validation'
import { captureEvent } from '@/lib/posthog/client'
import {
  buildAuthCrossLink,
  DEFAULT_POST_AUTH_ROUTE,
  POST_AUTH_REDIRECT_STORAGE_KEY,
  resolveAuthRedirect,
  resolvePostSignupDestination,
  VERIFY_FROM_SIGNUP_ROUTE,
} from '@/app/(auth)/auth-redirect'
import {
  AuthDivider,
  AuthField,
  AuthFormMessage,
  AuthHeader,
  AuthInput,
  AuthLegalFooter,
  AuthLoadingText,
  AuthNavPrompt,
  AuthSubmitButton,
  PasswordInput,
  SocialLoginButtons,
  SSOLoginButton,
} from '@/app/(auth)/components'
import {
  type AuthValidationTranslator,
  emailValidationMessage,
} from '@/app/(auth)/utils/email-validation-message'
import { useCaptureWhenReady } from '@/hooks/use-capture-when-ready'

const logger = createLogger('SignupForm')

const PASSWORD_VALIDATIONS = {
  minLength: { regex: /.{8,}/, message: 'passwordMinLength' },
  uppercase: { regex: /(?=.*?[A-Z])/, message: 'passwordUppercase' },
  lowercase: { regex: /(?=.*?[a-z])/, message: 'passwordLowercase' },
  number: { regex: /(?=.*?[0-9])/, message: 'passwordNumber' },
  special: { regex: /(?=.*?[#?!@$%^&*-])/, message: 'passwordSpecial' },
} as const

const NAME_VALIDATIONS = {
  required: {
    test: (value: string) => Boolean(value && typeof value === 'string'),
    message: 'nameRequired',
  },
  notEmpty: {
    test: (value: string) => value.trim().length > 0,
    message: 'nameEmpty',
  },
  validCharacters: {
    regex: /^[\p{L}\s\-']+$/u,
    message: 'nameCharacters',
  },
  noConsecutiveSpaces: {
    regex: /^(?!.*\s\s).*$/,
    message: 'nameConsecutiveSpaces',
  },
} as const

const validateEmailField = (
  emailValue: string,
  t: AuthValidationTranslator,
  locale: string
): string[] => {
  const errors: string[] = []

  if (!emailValue || !emailValue.trim()) {
    errors.push(t('emailRequired'))
    return errors
  }

  const validation = quickValidateEmail(emailValue.trim().toLowerCase())
  if (!validation.isValid) {
    errors.push(emailValidationMessage(validation.reason, t, locale))
  }

  return errors
}

interface SignupFormProps {
  githubAvailable: boolean
  googleAvailable: boolean
  microsoftAvailable: boolean
  emailSignupEnabled: boolean
  /** Server-derived: verification is enabled AND a mail provider is configured. */
  emailVerificationEnabled: boolean
}

/** omnyqra: UI copy from messages/<locale>/auth.json (Omnyqra/sim#43), see FORK.md */
function SignupFormContent({
  githubAvailable,
  googleAvailable,
  microsoftAvailable,
  emailSignupEnabled,
  emailVerificationEnabled,
}: SignupFormProps) {
  const router = useRouter()
  const searchParams = useSearchParams()
  const t = useTranslations('auth')
  const tv = useTranslations('auth.validation')
  const locale = useLocale()
  const { refetch: refetchSession } = useSession()
  const posthog = usePostHog()
  const { measurement, marketing } = useTrackingConsent()
  useCaptureWhenReady('signup_page_viewed', {})
  const [isLoading, setIsLoading] = useState(false)

  const [password, setPassword] = useState('')
  const [passwordErrors, setPasswordErrors] = useState<string[]>([])
  const [showValidationError, setShowValidationError] = useState(false)
  const [email, setEmail] = useState(() => searchParams.get('email') ?? '')
  const [emailError, setEmailError] = useState('')
  const [emailErrors, setEmailErrors] = useState<string[]>([])
  const [showEmailValidationError, setShowEmailValidationError] = useState(false)
  const [formError, setFormError] = useState<string | null>(null)
  const turnstileRef = useRef<TurnstileInstance>(null)
  const [turnstileSiteKey] = useState(() => getEnv('NEXT_PUBLIC_TURNSTILE_SITE_KEY'))
  const { rawCallbackUrl: rawRedirectUrl, isInviteFlow } = resolveAuthRedirect({
    redirect: searchParams.get('redirect'),
    callbackUrl: searchParams.get('callbackUrl'),
    inviteFlow: searchParams.get('invite_flow'),
  })
  const isValidRedirectUrl = rawRedirectUrl ? validateCallbackUrl(rawRedirectUrl) : false
  const invalidCallbackRef = useRef(false)
  if (rawRedirectUrl && !isValidRedirectUrl && !invalidCallbackRef.current) {
    invalidCallbackRef.current = true
    logger.warn('Invalid callback URL detected and blocked:', { url: rawRedirectUrl })
  }
  const redirectUrl = isValidRedirectUrl ? rawRedirectUrl : ''

  const [name, setName] = useState('')
  const [nameErrors, setNameErrors] = useState<string[]>([])
  const [showNameValidationError, setShowNameValidationError] = useState(false)

  const validatePassword = (passwordValue: string): string[] => {
    const errors: string[] = []

    if (!PASSWORD_VALIDATIONS.minLength.regex.test(passwordValue)) {
      errors.push(tv(PASSWORD_VALIDATIONS.minLength.message))
    }

    if (!PASSWORD_VALIDATIONS.uppercase.regex.test(passwordValue)) {
      errors.push(tv(PASSWORD_VALIDATIONS.uppercase.message))
    }

    if (!PASSWORD_VALIDATIONS.lowercase.regex.test(passwordValue)) {
      errors.push(tv(PASSWORD_VALIDATIONS.lowercase.message))
    }

    if (!PASSWORD_VALIDATIONS.number.regex.test(passwordValue)) {
      errors.push(tv(PASSWORD_VALIDATIONS.number.message))
    }

    if (!PASSWORD_VALIDATIONS.special.regex.test(passwordValue)) {
      errors.push(tv(PASSWORD_VALIDATIONS.special.message))
    }

    return errors
  }

  const validateName = (nameValue: string): string[] => {
    const errors: string[] = []

    if (!NAME_VALIDATIONS.required.test(nameValue)) {
      errors.push(tv(NAME_VALIDATIONS.required.message))
      return errors
    }

    if (!NAME_VALIDATIONS.notEmpty.test(nameValue)) {
      errors.push(tv(NAME_VALIDATIONS.notEmpty.message))
      return errors
    }

    if (!NAME_VALIDATIONS.validCharacters.regex.test(nameValue.trim())) {
      errors.push(tv(NAME_VALIDATIONS.validCharacters.message))
    }

    if (!NAME_VALIDATIONS.noConsecutiveSpaces.regex.test(nameValue)) {
      errors.push(tv(NAME_VALIDATIONS.noConsecutiveSpaces.message))
    }

    return errors
  }

  const handlePasswordChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newPassword = e.target.value
    setPassword(newPassword)

    const errors = validatePassword(newPassword)
    setPasswordErrors(errors)
    setShowValidationError(false)
  }

  const handleNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const rawValue = e.target.value
    setName(rawValue)

    const errors = validateName(rawValue)
    setNameErrors(errors)
    setShowNameValidationError(false)
  }

  const handleEmailChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newEmail = e.target.value
    setEmail(newEmail)

    const errors = validateEmailField(newEmail, tv, locale)
    setEmailErrors(errors)
    setShowEmailValidationError(false)

    if (emailError) {
      setEmailError('')
    }
  }

  async function onSubmit(e: React.FormEvent<HTMLFormElement>) {
    e.preventDefault()
    setIsLoading(true)

    const formData = new FormData(e.currentTarget)
    const emailValueRaw = formData.get('email') as string
    const emailValue = emailValueRaw.trim().toLowerCase()
    const passwordValue = formData.get('password') as string
    const nameValue = formData.get('name') as string

    const trimmedName = nameValue.trim()

    const nameValidationErrors = validateName(trimmedName)
    setNameErrors(nameValidationErrors)
    setShowNameValidationError(nameValidationErrors.length > 0)

    const emailValidationErrors = validateEmailField(emailValue, tv, locale)
    setEmailErrors(emailValidationErrors)
    setShowEmailValidationError(emailValidationErrors.length > 0)

    const errors = validatePassword(passwordValue)
    setPasswordErrors(errors)

    setShowValidationError(errors.length > 0)

    try {
      if (
        nameValidationErrors.length > 0 ||
        emailValidationErrors.length > 0 ||
        errors.length > 0
      ) {
        setIsLoading(false)
        return
      }

      if (trimmedName.length > 100) {
        setNameErrors([tv('nameTooLong')])
        setShowNameValidationError(true)
        setIsLoading(false)
        return
      }

      let token: string | undefined
      const widget = turnstileRef.current
      if (turnstileSiteKey && widget) {
        try {
          widget.reset()
          widget.execute()
          token = await widget.getResponsePromise()
        } catch {
          captureEvent(posthog, 'signup_failed', {
            error_code: 'captcha_client_failure',
          })
          setFormError(t('signup.captchaFailed'))
          setIsLoading(false)
          return
        }
      }

      setFormError(null)
      const response = await client.signUp.email(
        {
          email: emailValue,
          password: passwordValue,
          name: trimmedName,
        },
        {
          headers: {
            ...(token ? { 'x-captcha-response': token } : {}),
          },
          onError: (ctx) => {
            logger.warn('Signup error:', ctx.error)
            const errorMessage: string[] = [t('signup.failed')]

            let errorCode = 'unknown'
            if (ctx.error.code?.includes('USER_ALREADY_EXISTS')) {
              errorCode = 'user_already_exists'
              setEmailError(t('signup.userExists'))
            } else if (
              ctx.error.code?.includes('BAD_REQUEST') ||
              ctx.error.message?.includes('Email and password sign up is not enabled')
            ) {
              errorCode = 'signup_disabled'
              errorMessage.push(t('signup.emailSignupDisabled'))
              setEmailError(errorMessage[0])
            } else if (ctx.error.code?.includes('INVALID_EMAIL')) {
              errorCode = 'invalid_email'
              errorMessage.push(tv('emailInvalid'))
              setEmailError(errorMessage[0])
            } else if (ctx.error.code?.includes('PASSWORD_TOO_SHORT')) {
              errorCode = 'password_too_short'
              errorMessage.push(tv('passwordMinLength'))
              setPasswordErrors(errorMessage)
              setShowValidationError(true)
            } else if (ctx.error.code?.includes('PASSWORD_TOO_LONG')) {
              errorCode = 'password_too_long'
              errorMessage.push(tv('passwordMaxLength', { max: 128 }))
              setPasswordErrors(errorMessage)
              setShowValidationError(true)
            } else if (ctx.error.code?.includes('network')) {
              errorCode = 'network_error'
              errorMessage.push(t('common.networkError'))
              setPasswordErrors(errorMessage)
              setShowValidationError(true)
            } else if (ctx.error.code?.includes('rate limit')) {
              errorCode = 'rate_limited'
              errorMessage.push(t('common.rateLimited'))
              setPasswordErrors(errorMessage)
              setShowValidationError(true)
            } else {
              setPasswordErrors(errorMessage)
              setShowValidationError(true)
            }

            captureEvent(posthog, 'signup_failed', { error_code: errorCode })
          },
        }
      )

      if (!response || response.error) {
        setIsLoading(false)
        return
      }

      if (measurement) trackGoogleEvent('sign_up', { method: 'email' })
      if (marketing) trackFreebuffConversion('signup_completed', response.data.user.id)

      try {
        await refetchSession()
        logger.info('Session refreshed after successful signup')
      } catch (sessionError) {
        logger.error('Failed to refresh session after signup:', sessionError)
      }

      const destination = resolvePostSignupDestination({ emailVerificationEnabled, redirectUrl })

      if (typeof window !== 'undefined') {
        // Clear any leftover from an earlier signup in this tab — otherwise a
        // signup with no callbackUrl inherits the previous CLI/invite destination.
        sessionStorage.removeItem('verificationEmail')
        sessionStorage.removeItem(POST_AUTH_REDIRECT_STORAGE_KEY)

        if (destination.kind === 'verify') {
          sessionStorage.setItem('verificationEmail', emailValue)
          if (redirectUrl) sessionStorage.setItem(POST_AUTH_REDIRECT_STORAGE_KEY, redirectUrl)
        }
      }

      if (destination.kind === 'verify') {
        router.push(VERIFY_FROM_SIGNUP_ROUTE)
      } else {
        /** Match login/verification: refresh session-bound shells and their theme default. */
        window.location.href =
          destination.kind === 'redirect' ? destination.url : DEFAULT_POST_AUTH_ROUTE
      }
    } catch (error) {
      logger.error('Signup error:', error)
      setIsLoading(false)
    }
  }

  const ssoEnabled = isSsoEnabled
  const emailEnabled =
    !isFalsy(getEnv('NEXT_PUBLIC_EMAIL_PASSWORD_SIGNUP_ENABLED')) && emailSignupEnabled
  const hasSocial = githubAvailable || googleAvailable || microsoftAvailable
  const hasOnlySSO = ssoEnabled && !emailEnabled && !hasSocial
  const showBottomSection = hasSocial || (ssoEnabled && !hasOnlySSO)
  const showDivider = (emailEnabled || hasOnlySSO) && showBottomSection

  const nameFieldErrors = showNameValidationError && nameErrors.length > 0 ? nameErrors : []
  const emailHasError = Boolean(emailError) || (showEmailValidationError && emailErrors.length > 0)
  const emailFieldErrors =
    showEmailValidationError && emailErrors.length > 0
      ? emailErrors
      : emailError && !showEmailValidationError
        ? [emailError]
        : []
  const passwordFieldErrors = showValidationError && passwordErrors.length > 0 ? passwordErrors : []
  const canSubmit = name.trim().length > 0 && email.trim().length > 0 && password.length > 0

  return (
    <div className='space-y-6'>
      <AuthHeader title={t('signup.title')} description={t('signup.description')} />

      {hasOnlySSO && (
        <SSOLoginButton callbackURL={redirectUrl || DEFAULT_POST_AUTH_ROUTE} variant='primary' />
      )}

      {emailEnabled && (
        <form onSubmit={onSubmit} className='space-y-6'>
          <div className='space-y-5'>
            <AuthField htmlFor='name' label={t('signup.fullName')} errors={nameFieldErrors}>
              <AuthInput
                id='name'
                name='name'
                placeholder={t('signup.namePlaceholder')}
                type='text'
                autoCapitalize='words'
                autoComplete='name'
                title={t('signup.nameHint')}
                value={name}
                onChange={handleNameChange}
                error={nameFieldErrors.length > 0}
              />
            </AuthField>
            <AuthField htmlFor='email' label={t('common.email')} errors={emailFieldErrors}>
              <AuthInput
                id='email'
                name='email'
                placeholder={t('common.emailPlaceholder')}
                autoCapitalize='none'
                autoComplete='email'
                autoCorrect='off'
                value={email}
                onChange={handleEmailChange}
                error={emailHasError}
              />
            </AuthField>
            <AuthField htmlFor='password' label={t('common.password')} errors={passwordFieldErrors}>
              <PasswordInput
                id='password'
                name='password'
                autoCapitalize='none'
                autoComplete='new-password'
                placeholder={t('common.passwordPlaceholder')}
                autoCorrect='off'
                value={password}
                onChange={handlePasswordChange}
                error={passwordFieldErrors.length > 0}
              />
            </AuthField>
          </div>

          {turnstileSiteKey && (
            <Turnstile
              ref={turnstileRef}
              siteKey={turnstileSiteKey}
              options={{ execution: 'execute', appearance: 'execute' }}
            />
          )}

          {formError && (
            <AuthFormMessage type='error'>
              <p>{formError}</p>
            </AuthFormMessage>
          )}

          <AuthSubmitButton
            loading={isLoading}
            loadingLabel={t('signup.submitting')}
            disabled={!canSubmit}
          >
            {t('signup.submit')}
          </AuthSubmitButton>
        </form>
      )}

      {showDivider && <AuthDivider label={t('common.orContinueWith')} />}

      {showBottomSection && (
        <SocialLoginButtons
          view='signup'
          githubAvailable={githubAvailable}
          googleAvailable={googleAvailable}
          microsoftAvailable={microsoftAvailable}
          callbackURL={redirectUrl || DEFAULT_POST_AUTH_ROUTE}
        >
          {ssoEnabled && !hasOnlySSO && (
            <SSOLoginButton
              callbackURL={redirectUrl || DEFAULT_POST_AUTH_ROUTE}
              variant='outline'
            />
          )}
        </SocialLoginButtons>
      )}

      <AuthNavPrompt
        prompt={t('common.alreadyHaveAccount')}
        href={buildAuthCrossLink('/login', { callbackUrl: redirectUrl || null, isInviteFlow })}
        linkLabel={t('common.signIn')}
      />

      <AuthLegalFooter action='signUp' />
    </div>
  )
}

export default function SignupPage({
  githubAvailable,
  googleAvailable,
  microsoftAvailable,
  emailSignupEnabled,
  emailVerificationEnabled,
}: SignupFormProps) {
  return (
    <Suspense fallback={<AuthLoadingText />}>
      <SignupFormContent
        githubAvailable={githubAvailable}
        googleAvailable={googleAvailable}
        microsoftAvailable={microsoftAvailable}
        emailSignupEnabled={emailSignupEnabled}
        emailVerificationEnabled={emailVerificationEnabled}
      />
    </Suspense>
  )
}
